import { useNavigate } from "@tanstack/react-router";
import { Code, CreditCard } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck } from "@/components/animate-ui/icons/clipboard-check";
import { EllipsisVertical } from "@/components/animate-ui/icons/ellipsis-vertical";
import { LogOut } from "@/components/animate-ui/icons/log-out";
import { Settings } from "@/components/animate-ui/icons/settings";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import { logout } from "@/lib/auth/service";
import { getInitials } from "@/lib/utils/get-initials";
import { useOnboardingStore } from "@/stores/onboarding-store";

export function NavUser({
	user,
}: {
	user: {
		name: string;
		email: string;
		avatar?: string;
	};
}) {
	const { isMobile } = useSidebar();
	const navigate = useNavigate();
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const { openOnboarding } = useOnboardingStore();

	const handleLogout = async () => {
		setIsLoggingOut(true);
		try {
			await logout();
			toast.success("Logged out successfully");
			navigate({ to: "/" });
		} catch (error) {
			console.error("Logout error:", error);
			toast.error("Failed to logout. Please try again.");
		} finally {
			setIsLoggingOut(false);
		}
	};

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							<Avatar className="h-8 w-8 rounded-lg grayscale">
								<AvatarImage src={user.avatar} alt={user.name} />
								<AvatarFallback className="rounded-lg">{getInitials(user.name)}</AvatarFallback>
							</Avatar>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-medium">{user.name}</span>
								<span className="text-muted-foreground truncate text-xs">{user.email}</span>
							</div>
							<EllipsisVertical animateOnHover className="ml-auto size-4" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
						side={isMobile ? "bottom" : "right"}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuLabel className="p-0 font-normal">
							<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
								<Avatar className="h-8 w-8 rounded-lg">
									<AvatarImage src={user.avatar} alt={user.name} />
									<AvatarFallback className="rounded-lg">{getInitials(user.name)}</AvatarFallback>
								</Avatar>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-medium">{user.name}</span>
									<span className="text-muted-foreground truncate text-xs">{user.email}</span>
								</div>
							</div>
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							<DropdownMenuItem onClick={() => navigate({ to: "/billing" })}>
								<CreditCard />
								Billing
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => navigate({ to: "/account" })}>
								<Settings animateOnHover />
								Settings
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => navigate({ to: "/developer" })}>
								<Code />
								Developer
							</DropdownMenuItem>
							<DropdownMenuItem onClick={openOnboarding}>
								<ClipboardCheck animateOnHover />
								Onboarding
							</DropdownMenuItem>
							{/* <DropdownMenuItem
								onClick={() => window.open("https://github.com/m8tes-ai", "_blank")}
							>
								<Github />
								Github
							</DropdownMenuItem> */}
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={handleLogout} disabled={isLoggingOut}>
							<LogOut animateOnHover />
							{isLoggingOut ? "Logging out..." : "Log out"}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	);
}
